// src/pages/home/CalendarLog.js
import React, { useEffect, useState, useRef } from "react";
import "./CalendarLog.css";
import config from "../../config.json";

const DELETE_PASSWORD = config.password;

const START_LABEL = "Start time:";
const DURATION_LABEL = "Duration: (in minutes)";
const WALK_LOCATION_LABEL = "Location of walk:";
const CAT_LOCATION_LABEL = "Location:";

const TIME_ZONE = "America/New_York";

/**
 * Build "M/D/YYYY" in America/New_York (matches your seed IDs + matches your UI date filter reliably)
 */
function nyShortDateString(dateObj) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: TIME_ZONE,
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }).formatToParts(dateObj);

  const year = parts.find((p) => p.type === "year")?.value;
  const month = parts.find((p) => p.type === "month")?.value;
  const day = parts.find((p) => p.type === "day")?.value;

  // "M/D/YYYY"
  return `${month}/${day}/${year}`;
}

function estISODate(dateObj) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(dateObj);

  const year = parts.find((p) => p.type === "year")?.value;
  const month = parts.find((p) => p.type === "month")?.value;
  const day = parts.find((p) => p.type === "day")?.value;

  return `${year}-${month}-${day}`;
}

function formatDateStrings(dateObj) {
  const fullText = dateObj.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: TIME_ZONE,
  });

  // ✅ IMPORTANT: use our NY-short string, NOT toLocaleDateString directly
  const shortText = nyShortDateString(dateObj);

  const isoInput = estISODate(dateObj);

  return { fullText, shortText, isoInput };
}

function formatFullDateTime(timestamp) {
  const d = new Date(timestamp);
  return d.toLocaleString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: TIME_ZONE,
  });
}

function parseClockStringToDate(timeStr) {
  if (!timeStr) return null;

  const trimmed = String(timeStr).trim();

  const match = trimmed.match(
    /^(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\s*(AM|PM))?$/i
  );
  if (!match) return null;

  let hours = parseInt(match[1], 10);
  const mins = parseInt(match[2], 10);
  const ampm = match[4] ? match[4].toUpperCase() : null;

  if (Number.isNaN(hours) || Number.isNaN(mins)) return null;

  if (ampm) {
    if (ampm === "PM" && hours < 12) hours += 12;
    if (ampm === "AM" && hours === 12) hours = 0;
  }

  if (hours < 0 || hours > 23 || mins < 0 || mins > 59) return null;

  return new Date(1970, 0, 1, hours, mins, 0);
}

function formatClockTo12Hour(timeStr) {
  const d = parseClockStringToDate(timeStr);
  if (!d) return null;

  return d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
}

function addMinutesToClockString(timeStr, minutesToAdd) {
  const d = parseClockStringToDate(timeStr);
  if (!d) return null;

  d.setMinutes(d.getMinutes() + Number(minutesToAdd || 0));

  return d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
}

function clockStringToMinutes(timeStr) {
  const d = parseClockStringToDate(timeStr);
  if (!d) return null;
  return d.getHours() * 60 + d.getMinutes();
}

const DOG_ACTIVITY_TYPES = new Set([
  "Walk",
  "Indoor Activity",
  "Outdoor Activity",
  "Activity",
]);

/**
 * ✅ FIX: infer kind using "mode" signal first if present,
 * otherwise fall back to your old heuristic.
 *
 * This makes cat entries show even if their `type` is "Walk".
 */
function inferEntryKind(entry, defaultModeIsCat) {
  if (defaultModeIsCat) return "cat";

  const t = entry?.type;
  if (t && DOG_ACTIVITY_TYPES.has(t)) return "dog";
  return "cat";
}

function entryHasStartTime(entry) {
  return Boolean(
    entry?.questionnaire?.some((q) => q.question === START_LABEL && q.answer)
  );
}

function CalendarLog({ refreshKey, location, mode = "dog" }) {
  const normalizedMode = String(mode || "").toLowerCase();
  const isCatMode = normalizedMode.includes("cat");

  const today = new Date();
  const [selectedDate, setSelectedDate] = useState(today);
  const { fullText, shortText, isoInput } = formatDateStrings(selectedDate);

  const [logs, setLogs] = useState([]);
  const [expandedSet, setExpandedSet] = useState(() => new Set());

  const abortRef = useRef(null);
  const runIdRef = useRef(0);

  const cacheKeyRef = useRef("");

  // ✅ Pretty popup (same idea as your real CalendarLog / DataModal pattern)
  const [popup, setPopup] = useState({
    open: false,
    title: "",
    message: "",
  });

  const openPopup = (title, message) => {
    setPopup({ open: true, title: title || "", message: message || "" });
  };

  const closePopup = () => {
    setPopup((p) => ({ ...p, open: false }));
  };

  function setLogsAndCache(updater) {
    setLogs((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      try {
        const key = cacheKeyRef.current;
        if (key) localStorage.setItem(key, JSON.stringify(next));
      } catch {}
      return next;
    });
  }

  useEffect(() => {
    const cacheKey = `calendarLogs:v2:${isCatMode ? "cat" : "dog"}:${location}:${shortText}`;
    cacheKeyRef.current = cacheKey;

    // show cached logs immediately
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed)) setLogs(parsed);
        else setLogs([]);
      } else {
        setLogs([]);
      }
    } catch {
      setLogs([]);
    }

    setExpandedSet(new Set());

    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    const myRunId = ++runIdRef.current;

    async function fetchAnimals() {
      try {
        const endpoint = isCatMode ? "/api/cats" : "/api/dogs";
        const response = await fetch(endpoint, { signal: ctrl.signal });
        const data = await response.json();

        const foundLogs = [];

        for (const animal of data || []) {
          if (ctrl.signal.aborted) return;
          if (myRunId !== runIdRef.current) return;

          if (animal.location !== location) continue;

          const entries = Array.isArray(animal.walks)
            ? animal.walks
            : Array.isArray(animal.notes)
            ? animal.notes
            : [];

          for (const entry of entries) {
            const tsCandidate =
              entry?.startTimestamp ?? entry?.time ?? entry?.timeLogged ?? null;

            let matchesSelectedDay = false;

            if (tsCandidate != null) {
              const entryDayStr = nyShortDateString(new Date(Number(tsCandidate)));
              matchesSelectedDay = entryDayStr === shortText;
            } else if (entry?.id) {
              matchesSelectedDay = String(entry.id).includes(shortText);
            }

            if (!matchesSelectedDay) continue;

            const entryKind = inferEntryKind(entry, isCatMode);
            if (isCatMode && entryKind !== "cat") continue;

            const locationLabel =
              entryKind === "cat" ? CAT_LOCATION_LABEL : WALK_LOCATION_LABEL;

            const locQ = entry.questionnaire?.find(
              (q) => q.question === locationLabel
            );
            if (locQ && locQ.answer && locQ.answer !== location) continue;

            let startMinutes = null;
            const startQ = entry.questionnaire?.find(
              (q) => q.question === START_LABEL
            );
            if (startQ?.answer) startMinutes = clockStringToMinutes(startQ.answer);

            foundLogs.push({
              ...entry,
              animalName: animal.name,
              image: animal.image,
              animalId: animal.id,
              timeLogged: entry.time, // consistent with rest of component
              startMinutes,
              _kind: entryKind,
              _timed: entryHasStartTime(entry),
            });
          }
        }

        foundLogs.sort((a, b) => {
          const aMin = Number.isFinite(a.startMinutes)
            ? a.startMinutes
            : Number.POSITIVE_INFINITY;
          const bMin = Number.isFinite(b.startMinutes)
            ? b.startMinutes
            : Number.POSITIVE_INFINITY;

          if (aMin !== bMin) return bMin - aMin;
          return (b.timeLogged || 0) - (a.timeLogged || 0);
        });

        if (ctrl.signal.aborted) return;
        if (myRunId !== runIdRef.current) return;

        setLogsAndCache(foundLogs);
      } catch (err) {
        if (err?.name === "AbortError") return;
        console.error("Failed to fetch logs:", err);
        // optional: if you want the demo to show a nice error too
        // openPopup("Load failed", "Couldn’t load entries right now. Please try again.");
      }
    }

    fetchAnimals();

    return () => {
      ctrl.abort();
    };
  }, [shortText, refreshKey, location, isCatMode]);

  function handleDateChange(e) {
    if (!e.target.value) return;
    const [year, month, day] = e.target.value.split("-").map(Number);
    setSelectedDate(new Date(year, month - 1, day));
  }

  function toggleExpand(idx) {
    setExpandedSet((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }

  async function handleDelete(log) {
    const pwd = prompt("Enter delete password:");
    if (!pwd) return;

    // ✅ NEW: pretty popup if password wrong (same as real page)
    if (pwd !== DELETE_PASSWORD) {
      openPopup("Wrong password", "The password is incorrect.\nPlease try again.");
      return;
    }

    const isSameLog = (e) =>
      e?.animalId === log?.animalId &&
      e?.id === log?.id &&
      e?.timeLogged === log?.timeLogged;

    try {
      if (log._kind === "dog") {
        const res = await fetch("/api/delete", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            dogId: log.animalId,
            walkId: log.id,
            time: log.timeLogged,
          }),
        });

        if (!res.ok) {
          openPopup(
            "Delete failed",
            "Couldn’t delete this entry. Please try again."
          );
          return;
        }

        setLogsAndCache((prev) => prev.filter((entry) => !isSameLog(entry)));
        return;
      }

      let res = await fetch("/api/delete-cat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          catId: log.animalId,
          noteId: log.id,
          time: log.timeLogged,
        }),
      });

      if (!res.ok) {
        res = await fetch("/api/delete", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            catId: log.animalId,
            noteId: log.id,
            time: log.timeLogged,
          }),
        });
      }

      if (!res.ok) {
        openPopup(
          "Delete failed",
          "Couldn’t delete this entry. Please try again."
        );
        return;
      }

      setLogsAndCache((prev) => prev.filter((entry) => !isSameLog(entry)));
    } catch (err) {
      console.error("Error deleting log:", err);
      openPopup("Delete failed", "Something went wrong while deleting.");
    }
  }

  return (
    <div className="calendar-log-wrapper">
      <h2>{fullText}</h2>

      <input
        type="date"
        className="date-button"
        value={isoInput}
        onChange={handleDateChange}
      />

      {logs.length === 0 ? (
        <p style={{ opacity: 0.6 }}>No entries for this date.</p>
      ) : (
        logs.map((log, idx) => {
          const loggedAtFull = formatFullDateTime(log.timeLogged);

          let startTimeDisplay = null;
          let endTimeDisplay = null;

          const startQ = log.questionnaire?.find((q) => q.question === START_LABEL);
          const durationQ = log.questionnaire?.find(
            (q) => q.question === DURATION_LABEL
          );

          if (startQ?.answer) startTimeDisplay = formatClockTo12Hour(startQ.answer);

          if (startQ?.answer && durationQ?.answer) {
            const mins = parseInt(durationQ.answer, 10);
            if (!isNaN(mins) && mins > 0) {
              endTimeDisplay = addMinutesToClockString(startQ.answer, mins);
            }
          }

          const activityPhrase =
            log._kind === "cat"
              ? `had a note added `
              : log.type === "Walk"
              ? "was walked"
              : log.type === "Indoor Activity"
              ? "did an indoor activity"
              : log.type === "Outdoor Activity"
              ? "did an outdoor activity"
              : "had an activity";

          const activityNoun =
            log.type === "Walk"
              ? "walk"
              : log.type === "Indoor Activity"
              ? "indoor activity"
              : log.type === "Outdoor Activity"
              ? "outdoor activity"
              : "activity";

          const visibleQuestions =
            log.questionnaire?.filter((q) => {
              if (q.question === WALK_LOCATION_LABEL) return false;
              if (q.question === CAT_LOCATION_LABEL) return false;
              if (q.question === DURATION_LABEL) return false;
              return true;
            }) || [];

          const isExpanded = expandedSet.has(idx);

          return (
            <div
              key={`${log.animalId || "x"}:${log.id || idx}:${log.timeLogged || 0}`}
              className={`calendar-log-box ${log._kind === "cat" ? "cat-entry" : ""} ${
                isExpanded ? "expanded" : ""
              }`}
              onClick={() => toggleExpand(idx)}
            >
              <div className="calendar-log-summary">
                <img
                  src={
                    log.image ||
                    `https://robohash.org/${log.animalName}?set=set4&size=40x40`
                  }
                  alt={log.animalName}
                  className="calendar-log-avatar"
                />

                <div className="calendar-log-main-text">
                  {log.animalName} {activityPhrase}{" "}
                  {(log._kind === "dog" || (log._kind === "cat" && log._timed)) &&
                  startTimeDisplay
                    ? `at ${startTimeDisplay}`
                    : ""}
                </div>

                <span className={`calendar-log-arrow ${isExpanded ? "open" : ""}`}>
                  ▾
                </span>
              </div>

              {isExpanded && (
                <div className="calendar-log-details">
                  {visibleQuestions.map((q, i) => {
                    let answerDisplay = q.answer;

                    if (q.question === START_LABEL) {
                      answerDisplay = startTimeDisplay || q.answer;
                    }

                    if (Array.isArray(q.answer)) {
                      answerDisplay = q.answer.join(", ");
                    }

                    return (
                      <p key={i}>
                        <strong>{q.question}</strong> {answerDisplay}
                      </p>
                    );
                  })}

                  {(log._kind === "dog" || (log._kind === "cat" && log._timed)) &&
                    endTimeDisplay && (
                      <p>
                        <strong>
                          {log._kind === "cat"
                            ? "Ended time:"
                            : `Ended ${activityNoun} at:`}
                        </strong>{" "}
                        {endTimeDisplay}
                      </p>
                    )}

                  <p>
                    <strong>Logged by:</strong> {log.userFirstName}{" "}
                    {log.userLastName}
                  </p>

                  <p>
                    <strong>Logged at:</strong> {loggedAtFull}
                  </p>

                  <button
                    className="delete-log-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(log);
                    }}
                  >
                    Delete Entry
                  </button>
                </div>
              )}
            </div>
          );
        })
      )}

      {/* ✅ Pretty popup (same design pattern as real CalendarLog) */}
      {popup.open && (
        <div className="pretty-popup-overlay" onMouseDown={closePopup}>
          <div className="pretty-popup" onMouseDown={(e) => e.stopPropagation()}>
            {popup.title ? (
              <div className="pretty-popup-title">{popup.title}</div>
            ) : null}

            <div className="pretty-popup-body">
              {String(popup.message || "")
                .split("\n")
                .map((line, idx) => (
                  <div key={idx} className="pretty-popup-line">
                    {line === "" ? "\u00A0" : line}
                  </div>
                ))}
            </div>

            <button
              className="pretty-popup-close"
              onClick={closePopup}
              type="button"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default CalendarLog;
